import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  updateOnboardingData,
  updateStepData,
  nextStep,
  goToStep,
  updateProfileData,
  goBackToEditProfile,
  restoreFromBackup,
  completeOnboarding,
  resetOnboarding,
  resetForNewSignup,
  OnboardingData,
} from '../store/slices/onboardingSlice';

export const useOnboardingDataRedux = () => {
  const dispatch = useAppDispatch();
  const onboardingData = useAppSelector((state) => state.onboarding);

  // Update specific fields
  const updateData = (updates: Partial<OnboardingData>) => {
    dispatch(updateOnboardingData(updates));
  };

  // Update step data
  const updateStepDataAction = (step: number, stepData: Partial<OnboardingData>) => {
    dispatch(updateStepData({ step, data: stepData }));
  };

  // Move to next step
  const nextStepAction = (stepData?: Partial<OnboardingData>) => {
    dispatch(nextStep(stepData));
  };

  // Go to specific step
  const goToStepAction = (step: number) => {
    dispatch(goToStep(step));
  };

  // Update profile data without affecting payment information
  const updateProfileDataAction = (profileData: {
    email?: string;
    fullName?: string;
    phoneNumber?: string;
    currentStep?: number;
  }) => {
    dispatch(updateProfileData(profileData));
  };

  // Go back to edit profile while preserving payment data
  const goBackToEditProfileAction = (step: number) => {
    dispatch(goBackToEditProfile(step));
  };

  // Complete onboarding
  const completeOnboardingAction = () => {
    dispatch(completeOnboarding());
  };

  // Reset onboarding data
  const resetData = () => {
    dispatch(resetOnboarding());
  };

  // Reset for completely new signup (clears everything including payment)
  const resetForNewSignupAction = () => {
    dispatch(resetForNewSignup());
  };

  // Restore from localStorage backup if Redux state is lost
  const restoreFromBackupAction = () => {
    console.log('🔄 Restoring data from localStorage backup');
    dispatch(restoreFromBackup());
  };

  // Check if step is accessible
  const isStepAccessible = (step: number) => {
    if (step === 1) return true;
    return onboardingData.completedSteps[step - 2]; // Previous step must be completed
  };

  // Get completion percentage
  const getCompletionPercentage = () => {
    const completedCount = onboardingData.completedSteps.filter(Boolean).length;
    return Math.round((completedCount / 4) * 100);
  };

  // Check if payment has been completed successfully
  const hasPaymentCompleted = () => {
    return onboardingData.paymentStatus === 'completed' && onboardingData.stripePaymentIntentId;
  };

  // Check if user should be prevented from changing plan (payment completed)
  const shouldPreventPlanChange = () => {
    return hasPaymentCompleted() && !onboardingData.profileCreated;
  };

  return {
    data: onboardingData,
    isLoaded: true, // No persistence - always loaded
    updateData,
    updateStepData: updateStepDataAction,
    nextStep: nextStepAction,
    goToStep: goToStepAction,
    updateProfileData: updateProfileDataAction,
    goBackToEditProfile: goBackToEditProfileAction,
    completeOnboarding: completeOnboardingAction,
    resetData,
    resetForNewSignup: resetForNewSignupAction,
    restoreFromBackup: restoreFromBackupAction,
    isStepAccessible,
    getCompletionPercentage,
    hasPaymentCompleted,
    shouldPreventPlanChange,
    
    // Convenience getters for specific step data
    signupData: {
      email: onboardingData.email,
      password: onboardingData.password,
      fullName: onboardingData.fullName,
      acceptedPolicy: onboardingData.acceptedPolicy,
      clerkUserId: onboardingData.clerkUserId,
    },
    
    pricingData: {
      selectedPlan: onboardingData.selectedPlan,
      planPrice: onboardingData.planPrice,
      planFeatures: onboardingData.planFeatures,
    },
    
    paymentData: {
      paymentMethod: onboardingData.paymentMethod,
      paymentStatus: onboardingData.paymentStatus,
      stripePaymentIntentId: onboardingData.stripePaymentIntentId,
    },
    
    completionData: {
      phoneNumber: onboardingData.phoneNumber,
      smsConsent: onboardingData.smsConsent,
      profileCreated: onboardingData.profileCreated,
      paidUserDataAdded: onboardingData.paidUserDataAdded,
    },
  };
}; 